import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Container from '../components/common/Container';
import { Heading, Subheading } from '../components/common/TextComponents';
import Button from '../components/common/Button';
import { uploadVideo, saveVideoSubmission, saveVideoToUserProfile } from '../services/firebase';
import { useAuthContext } from '../hooks/useAuthContext';
import { useAnalytics } from '../hooks/useAnalytics';

const VideoRecordingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoBlobSize, setVideoBlobSize] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  const uploadTimeoutRef = useRef(null);
  const streamRef = useRef(null); // Add a ref to keep track of the media stream
  const { trackVideoUpload, trackPage } = useAnalytics();
  
  // Get submission ID and video type from location state
  const submissionId = location.state?.submissionId || '';
  const returnToDashboard = location.state?.returnToDashboard || false;
  const videoType = location.state?.videoType || 'intro';
  
  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Setup listeners for progress updates
  useEffect(() => {
    // Create a custom event listener for upload progress
    const handleUploadProgress = (event) => {
      if (event.detail && typeof event.detail.progress === 'number') {
        setUploadProgress(event.detail.progress);
      }
    };
    
    window.addEventListener('video-upload-progress', handleUploadProgress);
    
    return () => {
      window.removeEventListener('video-upload-progress', handleUploadProgress);
      
      // Cleanup camera
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Clear timers
      if (timerRef.current) clearInterval(timerRef.current);
      if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);
    };
  }, []);
  
  // Track page view
  useEffect(() => {
    trackPage('video_recording', `Video Recording - ${videoType || 'intro'}`);
  }, [videoType, trackPage]);
  
  // Questions/prompts based on videoType
  const getPromptForVideoType = () => {
    switch(videoType) {
      case 'experience':
        return "Describe your most relevant work experience and what you learned from it.";
      case 'skills':
        return "What are your key skills and how have you applied them in your career?";
      case 'why':
        return "Why should employers choose you? What makes you stand out from other candidates?";
      case 'goals':
        return "What are your career goals for the next 3-5 years?";
      case 'intro':
      default:
        return "Tell us about yourself and the type of role you're looking for.";
    }
  };
  
  const questionPrompt = getPromptForVideoType();
  
  const startCamera = async () => {
    try {
      // Stop any existing streams first
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => track.stop());
      }

      // Request media with specific audio constraints for better quality
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user"
        }, 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 2
        }
      });
      
      // Save the stream reference
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      return stream;
    } catch (err) {
      console.error("Error accessing camera:", err);
      setError("Camera access denied. Please allow camera access to record your video.");
      return null;
    }
  };
  
  const startRecording = async () => {
    setError('');
    setProgress(0);
    setRecordingTime(0);
    
    // Reset any previous recording
    setVideoBlob(null);
    setVideoBlobSize(0);
    setShowPreview(false);
    
    // Start camera
    const stream = await startCamera();
    if (!stream) return;
    
    // Initialize media recorder with the stream
    try {
      // Try different codecs in order of preference
      const mimeTypes = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=h264,opus',
        'video/webm',
        'video/mp4'
      ];
      
      let options = { 
        mimeType: mimeTypes[0],
        audioBitsPerSecond: 128000, // Better audio quality
        videoBitsPerSecond: 2500000 // Reasonable video quality
      };
      
      let mediaRecorder = null;
      
      // Try each mime type until we find one that works
      for (let i = 0; i < mimeTypes.length; i++) {
        try {
          options.mimeType = mimeTypes[i];
          mediaRecorder = new MediaRecorder(stream, options);
          console.log("Using mime type:", mimeTypes[i]);
          break;
        } catch (e) {
          console.log(`${mimeTypes[i]} is not supported, trying next format`);
          if (i === mimeTypes.length - 1) {
            throw new Error("No supported video format found");
          }
        }
      }
      
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
          console.log(`Received data chunk: ${e.data.size} bytes`);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Create a blob with the collected chunks
        const blob = new Blob(chunks, { type: mediaRecorder.mimeType });
        const blobSizeInMB = blob.size / (1024 * 1024);
        console.log(`Recording complete. Total size: ${blobSizeInMB.toFixed(2)} MB, Mime type: ${blob.type}`);
        setVideoBlob(blob);
        setVideoBlobSize(blobSizeInMB.toFixed(2));
        setShowPreview(true);
        
        // Stop camera after recording is complete
        if (streamRef.current) {
          const tracks = streamRef.current.getTracks();
          tracks.forEach(track => track.stop());
          streamRef.current = null;
          
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
        }
      };
      
      // Start recording with a higher timeslice value (2000ms) to reduce chunking
      mediaRecorder.start(2000);
      setRecording(true);
      console.log("Recording started");
      
      // Start timer to track recording duration
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Calculate progress percentage (3 minutes = 180 seconds)
          const newProgress = (newTime / 180) * 100;
          setProgress(Math.min(newProgress, 100));
          return newTime;
        });
      }, 1000);
      
      // Automatically stop recording after 3 minutes (180000 ms)
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      }, 180000);
    } catch (error) {
      console.error("Error initializing media recorder:", error);
      setError("Your browser doesn't support the necessary video recording features. Please try a different browser or device.");
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log("Stopping recording");
      mediaRecorderRef.current.stop();
      setRecording(false);
      
      // Clear the recording timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };
  
  const retakeVideo = () => {
    setShowPreview(false);
    setVideoBlob(null);
    setVideoBlobSize(0);
    startRecording();
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Track upload progress
  const trackUploadProgress = (progress) => {
    // Use a custom event to update progress in the component
    const progressEvent = new CustomEvent('video-upload-progress', {
      detail: { progress }
    });
    window.dispatchEvent(progressEvent);
  };
  
  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to submit');
      return;
    }
    
    if (!videoBlob) {
      setError('Please record or re-record your video before submitting.');
      return;
    }
    
    if (isSubmitting) return; // Prevent double submission
    
    setIsSubmitting(true);
    setError('');
    setUploadProgress(0); // Reset progress
    
    // Optional: Add a timeout for upload
    // const timeoutDuration = 120000; // 2-minute timeout
    // uploadTimeoutRef.current = setTimeout(() => {
    //   setError('Upload timed out. Please check your connection and try again.');
    //   setIsSubmitting(false);
    //   clearInterval(progressInterval);
    // }, timeoutDuration);
    
    try {
      console.log(`Starting upload process for user ${user.uid}, video type: ${videoType}`);
      console.log(`Video blob: ${videoBlob.size} bytes, type: ${videoBlob.type}`);
      
      // Setup a polling mechanism to check upload progress (less reliable than listener)
      // let progressInterval = setInterval(() => {
      //   console.log("Checking upload progress...");
      // }, 2000);
      
      // REMOVED check for submissionId
      // if (!submissionId) {
      //   throw new Error("Missing submission ID. Please go back and complete the quiz first.");
      // }
      
      trackUploadProgress(10);
      
      // Upload the video to Firebase Storage
      console.log(`Uploading video for user ${user.uid}`);
      
      // Create a simpler, more direct upload
      const videoResult = await simpleUploadVideo(user.uid, videoBlob);
      
      // Clear intervals and timeouts if used
      // clearInterval(progressInterval);
      // if (uploadTimeoutRef.current) {
      //   clearTimeout(uploadTimeoutRef.current);
      //   uploadTimeoutRef.current = null;
      // }
      
      if (!videoResult.success) {
        throw new Error(videoResult.error || "Failed to upload video");
      }
      
      console.log("Video uploaded successfully:", videoResult);
      trackUploadProgress(80);
      
      // Track the successful video upload
      await trackVideoUpload(videoType || 'intro');
      
      // REMOVED saving to submission document for backward compatibility
      // if (videoType === 'intro') {
      //   console.log(`Updating submission ${submissionId} with video data`);
      //   const saveResult = await saveVideoSubmission(
      //     user.uid, 
      //     submissionId, 
      //     {
      //       videoUrl: videoResult.videoUrl,
      //       storagePath: videoResult.storagePath,
      //       timestamp: videoResult.timestamp
      //     }
      //   );
        
      //   if (!saveResult.success) {
      //     throw new Error(saveResult.error || "Failed to save video information");
      //   }
      // }
      
      // Save video metadata directly to user profile (this is the main path now)
      console.log(`Saving ${videoType} video to user profile for ${user.uid}`);
      const profileSaveResult = await saveVideoToUserProfile(
        user.uid,
        {
          videoUrl: videoResult.videoUrl,
          storagePath: videoResult.storagePath,
          timestamp: videoResult.timestamp
        },
        videoType // Use the videoType passed from location state
      );
      
      if (!profileSaveResult.success) {
        console.error("Error saving to user profile:", profileSaveResult.error);
        // Log error but continue as upload was successful
        // setError(`Video uploaded but failed to update profile: ${profileSaveResult.error}`);
        // Consider if you want to halt here or inform the user differently
      }
      
      console.log("Video metadata saved successfully");
      trackUploadProgress(100);
      
      // Short delay to show 100% progress before navigating
      setTimeout(() => {
        // Navigate to dashboard - user is already authenticated
        navigate('/dashboard');
      }, 500);
      
    } catch (error) {
      console.error("Error submitting video:", error);
      setError(error.message || "An unexpected error occurred. Please try again later.");
      setIsSubmitting(false);
      
      // Clear the timeout since we handled the error
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
    }
  };
  
  // A simpler implementation for uploading video 
  // that doesn't rely on complex progress tracking
  const simpleUploadVideo = async (userId, videoBlob) => {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `user_videos/${userId}_${timestamp}.webm`;
      
      console.log(`Using simple upload for video: ${filename}`);
      
      // Import the storage functions directly
      const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      const videoRef = ref(storage, filename);
      
      // Use the simpler uploadBytes function
      trackUploadProgress(20); // Start progress
      
      // Do the upload
      console.log("Starting direct upload...");
      const uploadResult = await uploadBytes(videoRef, videoBlob);
      console.log("Upload completed:", uploadResult);
      
      trackUploadProgress(70); // Upload complete
      
      // Get the URL
      const downloadURL = await getDownloadURL(videoRef);
      console.log("Download URL:", downloadURL);
      
      return {
        success: true,
        videoUrl: downloadURL,
        storagePath: filename,
        timestamp: timestamp
      };
    } catch (error) {
      console.error("Simple upload failed:", error);
      return { success: false, error: error.message };
    }
  };
  
  return (
    <div className="py-24 relative sm:py-32">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
      
      <Container>
        <div className="mx-auto max-w-2xl text-center relative">
          <Subheading>{videoType === 'intro' ? 'Video Introduction' : `${videoType.charAt(0).toUpperCase() + videoType.slice(1)} Video`}</Subheading>
          <Heading as="h2" className="mt-2">
            {returnToDashboard ? `Update your ${videoType} video` : "Tell us about yourself"}
          </Heading>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Please record a short video (up to 3 minutes) answering the following question:
          </p>
          <p className="mt-4 text-xl font-semibold text-amber-600">
            {questionPrompt}
          </p>
        </div>
        
        <div className="mt-16 mx-auto max-w-3xl bg-white rounded-3xl shadow-xl p-8 relative z-10">
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg text-red-700">
              {error}
            </div>
          )}
          
          <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden mb-8">
            {showPreview ? (
              <video 
                ref={videoRef}
                src={videoBlob ? URL.createObjectURL(videoBlob) : ''} 
                controls
                className="w-full h-full object-cover"
              />
            ) : (
              <video 
                ref={videoRef} 
                autoPlay 
                muted={true} // Always mute during recording to prevent audio feedback
                className="w-full h-full object-cover"
                playsInline
              />
            )}
            
            {!recording && !showPreview && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <p className="text-white text-xl">Ready to record your video</p>
              </div>
            )}
            
            {recording && (
              <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-70 p-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="h-3 w-3 rounded-full bg-red-600 mr-2 animate-pulse"></div>
                    <span className="text-white text-sm font-medium">Recording: {formatTime(recordingTime)}</span>
                  </div>
                  <span className="text-white text-sm">{Math.min(Math.round(recordingTime / 180 * 100), 100)}%</span>
                </div>
                <div className="w-full bg-gray-600 h-1 mt-2 rounded-full">
                  <div 
                    className="bg-amber-500 h-1 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          
          {/* Display video size when in preview mode */}
          {showPreview && videoBlobSize > 0 && (
            <p className="text-sm text-gray-500 mb-4 text-center">
              Video size: {videoBlobSize} MB {videoBlobSize > 30 && "(Large video files may take longer to upload)"}
            </p>
          )}
          
          <div className="flex justify-center gap-4">
            {!recording && !showPreview && (
              <Button onClick={startRecording}>
                Start Recording
              </Button>
            )}
            
            {recording && (
              <Button 
                variant="secondary" 
                onClick={stopRecording}
              >
                Stop Recording
              </Button>
            )}
            
            {showPreview && (
              <>
                <Button 
                  variant="secondary" 
                  onClick={retakeVideo}
                >
                  Record Again
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Uploading...' : 'Submit Video'}
                </Button>
              </>
            )}
          </div>
          
          {isSubmitting && (
            <div className="mt-4">
              <p className="text-center text-sm text-gray-600">
                Uploading your video... This may take a moment.
                {uploadProgress > 0 && ` (${Math.round(uploadProgress)}% complete)`}
              </p>
              {/* Add progress bar for upload */}
              <div className="w-full bg-gray-200 h-2 mt-2 rounded-full">
                <div 
                  className="bg-amber-500 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="mt-8 text-sm text-gray-500">
            <p>Tips for a great video:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Find a quiet place with good lighting</li>
              <li>Speak clearly and at a moderate pace</li>
              <li>Be authentic and showcase your personality</li>
              <li>Keep your response under 3 minutes</li>
              <li>Try to keep the recording under 30MB for faster upload</li>
            </ul>
          </div>
          
          {/* Skip video recording for now */}
          <div className="mt-8 flex justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
            >
              Skip video for now
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default VideoRecordingPage;